import React from 'react';
import { noop } from 'lodash';

import useAxiosErrorHandler from './useAxiosErrorHandler';
import useIsMounted from './useIsMounted';

const useFlatList = <T extends any>(
  makeRequest: (offset: number, limit: number) => Promise<T[]>,
  limit = 10
) => {
  const [ended, setEnded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [data, setData] = React.useState<T[]>([]);
  const axiosErrorHandler = useAxiosErrorHandler();
  const isMounted = useIsMounted();

  const handleRefresh = React.useCallback(async () => {
    try {
      setRefreshing(true);
      setEnded(false);
      const newData = await makeRequest(0, limit);
      setData(newData);
      setOffset(limit);
      setRefreshing(false);
    } catch (e) {
      if (isMounted) {
        setRefreshing(false);
      }
      axiosErrorHandler(e);
    }
  }, [axiosErrorHandler, isMounted, limit, makeRequest]);

  const handleEndReached = React.useCallback(async () => {
    if (loading || ended) {
      return;
    }
    try {
      setLoading(true);
      const newData = await makeRequest(offset, limit);
      if (newData.length === 0) {
        setEnded(true);
        setData([...data]);
      } else {
        setData([...data, ...newData]);
        setOffset(limit + offset);
      }
    } catch (e) {
      axiosErrorHandler(e);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  }, [
    axiosErrorHandler,
    data,
    ended,
    isMounted,
    limit,
    loading,
    makeRequest,
    offset,
  ]);

  React.useEffect(() => {
    handleRefresh().catch(noop);
  }, [handleRefresh]);

  return React.useMemo(
    () => ({
      data,
      handleEndReached,
      handleRefresh,
      refreshing,
      loading,
    }),
    [data, handleEndReached, handleRefresh, loading, refreshing]
  );
};

export default useFlatList;
